import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { TextField, Checkbox } from '@iclinic/design-system';
import { useRifm } from 'rifm';

import { OptInAddressData } from 'features/free-samples/services/types';
import * as S from './AddressFormFIelds.styles';
import BRAZIL_STATES_INITIALS from 'shared/constants/brStates';
import { cepFormatter, phoneFormatter } from 'shared/utils/formatters';
import { useGetZipCodeAddress } from 'features/CFMUpdate/components/hooks';
import { AddressField } from './AddressField';

const AddressFormFields = () => {
  const { getAddress } = useGetZipCodeAddress();

  const {
    values,
    handleChange,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<OptInAddressData>();

  const zipCodeMask = useRifm({
    value: values.zip_code,

    onChange: (value: string) => {
      setFieldValue('zip_code', value);
    },

    format: cepFormatter,
  });

  const phoneMask = useRifm({
    value: values.phone_number,
    onChange: (value: string) => {
      setFieldValue('phone_number', value);
    },

    format: phoneFormatter,
  });

  useEffect(() => {
    const rawZip = (values.zip_code || '').replace(/\D/g, '');
    if (rawZip.length === 8) {
      getAddress(rawZip);
    }
  }, [values.zip_code, getAddress]);

  useEffect(() => {
    if ((values.zip_code || '').length === 9) {
      setFieldTouched('zip_code', true);
    }
  }, [values.zip_code, setFieldTouched]);

  return (
    <S.Root>
      <S.FieldsWrapper>
        <TextField
          id="phone_number"
          label="Telefone"
          name="phone_number"
          placeholder="(00)00000.0000"
          value={phoneMask.value}
          onChange={phoneMask.onChange}
          error={!!(errors.phone_number && touched.phone_number)}
          helperText={touched.phone_number && errors.phone_number}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]',
          }}
        />

        <TextField
          id="cep"
          label="CEP"
          placeholder="Digite aqui o CEP para entrega na sua clínica"
          name="zip_code"
          value={zipCodeMask.value}
          onChange={zipCodeMask.onChange}
          error={touched.zip_code && !!errors.zip_code}
          helperText={touched.zip_code && errors.zip_code}
          style={{ gridArea: 'cep' }}
        />

        <S.CepHelper
          href="https://buscacepinter.correios.com.br/"
          target="_blank"
          rel="noreferrer"
          style={{ gridArea: 'cepHelper' }}
        >
          Não sei meu CEP
        </S.CepHelper>

        <AddressField
          id="address"
          name="address"
          label="Endereço"
          placeholder="Rua Professora Maria Aparecida"
          gridArea="address"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <AddressField
          id="number"
          name="number"
          label="Número"
          placeholder="515"
          gridArea="number"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <S.TextComplement
          id="complement"
          label="Complemento"
          name="complement"
          placeholder="Apartamento 5"
          value={values.complement}
          onChange={handleChange}
          style={{ gridArea: 'complement' }}
        />

        <AddressField
          id="neighborhood"
          name="neighborhood"
          label="Bairro"
          placeholder="Centro"
          gridArea="neighborhood"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <AddressField
          id="city"
          name="city"
          label="Cidade"
          placeholder="Ribeirão Preto"
          gridArea="city"
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
        />

        <TextField
          id="state"
          label="Estado"
          name="state"
          value={values.state}
          onChange={handleChange}
          error={touched.state && !!errors.state}
          helperText={touched.state && errors.state}
          select
          SelectProps={{
            native: true,
          }}
          style={{ gridArea: 'state' }}
        >
          <option value="">Selecione</option>
          {BRAZIL_STATES_INITIALS.map((state) => (
            <option value={state} key={state}>
              {state}
            </option>
          ))}
        </TextField>
      </S.FieldsWrapper>

      <S.CheckboxWrapper
        className={
          touched.is_clinic_address && errors.is_clinic_address ? 'error' : ''
        }
      >
        <Checkbox
          id="is_clinic_address"
          name="is_clinic_address"
          data-testid="is_clinic_address"
          checked={values.is_clinic_address}
          onChange={handleChange}
          label="Confirmo que os dados fornecidos são da minha clínica"
        />

        <S.BodyDescription variant="xs">
          Ao confirmar, você concorda com nossos{' '}
          <a
            href="https://suporte.iclinic.com.br/pt-br/termo-de-autoriza%C3%A7%C3%A3o-e-consentimento-rx-pro"
            target="_blank"
            rel="noreferrer"
          >
            Termos de Uso
          </a>{' '}
          e{' '}
          <a
            href="https://www.iclinic.com.br/politica-de-privacidade/"
            target="_blank"
            rel="noreferrer"
          >
            Política de Privacidade
          </a>
          .
          <br />
          Você pode cancelar à qualquer momento.
        </S.BodyDescription>
      </S.CheckboxWrapper>
    </S.Root>
  );
};

export default AddressFormFields;
